import { Badge, FormActions } from "@solomon-labs/design/components/ui";
import React, { useState } from "react";

import { AddressField } from "@/pages/AddPosition/components/AddressField";
import { FormErrorRetry } from "@/pages/shared/FormErrorRetry";

import {
    checkAddDestinationForm,
    toSavedDestinationInput
} from "./destinationInput";
import { LabelField } from "./LabelField";

interface AddDestinationFormProps {
    /**
     * Called with the validated `{ name, address }`. The page owns the
     * `useCreateSavedDestination` mutation and what happens on success.
     */
    onAdd: (input: { name: string; address: string }) => void;
    /** Leave the flow without saving. */
    onCancel: () => void;
    /** A save is in flight — fields and actions go inert. */
    busy?: boolean;
    /** Why the last save failed, shown inline above the actions (no toast). */
    error?: string | null;
    /** Offered beside `error` when the save never reached the server: sends
     *  the same save again. */
    onRetry?: () => void;
}

/**
 * The add-reward-destination flow on `/reward-destinations/add` — one bordered
 * panel (the Add a Position content frame, without its step rail since this is
 * a single step) holding the two light-bar fields (label inside, the DS Input prefix
 * recipe) and the
 * Save / Cancel pair. Validation and the `label || address` name fallback live in the pure
 * `destinationInput` helpers. (Add a Position adds a destination by typing
 * its address straight into the picker, `SavedDestinationCombobox`.)
 */
export function AddDestinationForm({
    onAdd,
    onCancel,
    busy = false,
    error = null,
    onRetry
}: AddDestinationFormProps): React.ReactElement {
    const [address, setAddress] = useState("");
    const [addressTouched, setAddressTouched] = useState(false);
    const [label, setLabel] = useState("");

    const { valid, showInvalid, warnExchange } = checkAddDestinationForm({
        address,
        label,
        addressTouched
    });

    const submit = (): void => {
        if (!valid || busy) return;
        onAdd(toSavedDestinationInput(address, label));
    };

    return (
        <div className="flex flex-col border border-line-base bg-surface-ground px-6 py-6">
            {/* max-w-3xl, a step past the shared field measure: a full
                address still fits beside the badge at the field's end. */}
            <div className="flex w-full max-w-3xl flex-col">
                <AddressField
                    label="Solana Address"
                    value={address}
                    onChange={setAddress}
                    onBlur={() => setAddressTouched(true)}
                    error={showInvalid}
                    disabled={busy}
                    placeholder="Gj6RvP2qT8nHk5LxW3bMfCdEz…"
                    fill
                    // What is wrong (or worth a second look) is a badge at
                    // the field's end, as in the routing editor's fields.
                    endAddon={
                        showInvalid ? (
                            <Badge variant="fill" color="error">
                                Invalid Address
                            </Badge>
                        ) : warnExchange ? (
                            <Badge variant="fill" color="warning">
                                Exchange Address
                            </Badge>
                        ) : undefined
                    }
                />

                <LabelField
                    className="mt-4"
                    value={label}
                    onChange={setLabel}
                    disabled={busy}
                    fill
                />
            </div>

            {error ? (
                <FormErrorRetry
                    className="mt-4"
                    error={error}
                    onRetry={onRetry}
                    busy={busy}
                />
            ) : null}

            <FormActions
                className="mt-8"
                size="md"
                primaryLabel={busy ? "Saving…" : "Save Destination"}
                onPrimary={submit}
                primaryDisabled={!valid || busy}
                cancelLabel="Cancel"
                onCancel={onCancel}
                cancelDisabled={busy}
            />
        </div>
    );
}
